// src/Components/TopBar/TopBar.js

import React, { useState, useEffect, useRef } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GlobalStyles from '@mui/material/GlobalStyles';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

// Firebase
import { signOut, onIdTokenChanged } from 'firebase/auth';
import { doc, onSnapshot, collection } from 'firebase/firestore';
import { auth, db } from '../../Firebase/firebase';
import { useNavigate, useLocation } from 'react-router-dom';

// Custom Imports
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import Logo from '../../assets/images/logo.png';
import { MenuFilter, MenuSubscribe } from '../../assets/icons/CustomIcons';
import { MenuInitialView } from '../../assets/icons/CustomIconsAdd1';
import { MenuResetView } from '../../assets/icons/CustomIconsAdd4';
import { useMapInitialView } from '../../Hooks/useMapInitialView';
import SearchBar from './SearchBar';

import SubscribeDialog from './SubscribeDialog';

export default function TopBar({
  activeMenu,
  setShowPost,
  setEventsDetails,
  setEditPost,
  setActiveMenu,
  setAuthModal,
  setShowAdmin,
  setShowPromo,
  isLocked,
  resetMap,
  setShowFilter,
  resetFilter,
  setSearchMarker,
  setSelectedCategory,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // 1. Current Path Detection
  const currentPath = (location.pathname + location.hash).toLowerCase();
  const authKeywords = ['/signin', '/signup', '/verify-email', '/login'];
  const isAuthRoute = authKeywords.some((route) => currentPath.includes(route));

  // 2. Track entry path dynamically supporting HashRouter & BrowserRouter
  useState(() => {
    const stored = sessionStorage.getItem('loktr_entry_path');
    if (!stored) {
      const initialPath = location.pathname + location.hash;
      sessionStorage.setItem('loktr_entry_path', initialPath);
    }
  });

  // 3. User State
  const [currentUser, setCurrentUser] = useState(auth.currentUser);

  const [dbUserName, setDbUserName] = useState('');

  // Determine if the user signed in with Google
  const isGoogleUser = currentUser?.providerData?.some(
    (p) => p.providerId === 'google.com',
  );

  // 4. Menu Visibility - Home is now always visible for all users
  const showHomeMenu = true;

  const [anchorEl, setAnchorEl] = useState(null);
  const { viewConfig } = useMapInitialView();

  const [userPermissions, setUserPermissions] = useState({
    isListed: false,
    canUpdate: false,
    canPromo: false,
    isAdmin: false,
  });

  // Alert States
  const [showLockAlert, setShowLockAlert] = useState(false);

  // Subscribe Dialog State
  const [showSubscribe, setShowSubscribe] = useState(false);

  // Location Prompt and Loading States
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  // Unified State Machine Tracker
  const handledStateRef = useRef(null);
  const prevUserRef = useRef(currentUser);

  const [mainWebSiteUrl, setMainWebSiteUrl] = useState('');
  const [clientLogoUrl, setClientLogoUrl] = useState('');
  const [clientLogoScale, setClientLogoScale] = useState(100);

  // Initial Views & Filters dropdowns
  const [initialViews, setInitialViews] = useState([]);
  const [initialViewAnchorEl, setInitialViewAnchorEl] = useState(null);
  const [activeView, setActiveView] = useState(null);

  // States for Unified Filter Dropdown
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [categories, setCategories] = useState([]);

  // State to hold the user's detected location
  const [userLocationView, setUserLocationView] = useState(null);

  // --- REFS ---
  const unsubWebRef = useRef(null);
  const unsubLogoRef = useRef(null);
  const unsubPermsRef = useRef(null);
  const unsubViewsRef = useRef(null);

  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    const unsubAuth = onIdTokenChanged(auth, (user) => {
      if (user) {
        // Prevent the authenticated menu from displaying while unverified
        const isPasswordUser = user.providerData?.some(
          (p) => p.providerId === 'password',
        );
        if (isPasswordUser && !user.emailVerified) {
          setCurrentUser(null);
          setUserPermissions({
            isListed: false,
            canUpdate: false,
            canPromo: false,
            isAdmin: false,
          });
          return;
        }

        const photo = user.photoURL || user.providerData?.[0]?.photoURL || null;
        setCurrentUser({ ...user, photoURL: photo });
      } else {
        setCurrentUser(null);
        setUserPermissions({
          isListed: false,
          canUpdate: false,
          canPromo: false,
          isAdmin: false,
        });
      }
    });

    unsubWebRef.current = onSnapshot(doc(db, 'settings', 'MainWebSite'), (docSnap) => {
      if (docSnap.exists()) setMainWebSiteUrl(docSnap.data().url || '');
      else setMainWebSiteUrl('');
    });

    unsubLogoRef.current = onSnapshot(doc(db, 'settings', 'MainLogo'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setClientLogoUrl(data.url || '');
        setClientLogoScale(typeof data.scale === 'number' ? data.scale : 100);
      } else {
        setClientLogoUrl('');
        setClientLogoScale(100);
      }
    });

    // Fetch Initial Views for dropdown
    unsubViewsRef.current = onSnapshot(collection(db, 'initialViews'), (snapshot) => {
      const viewsData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      viewsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setInitialViews(viewsData);
    });

    return () => {
      unsubAuth();
      if (unsubWebRef.current) unsubWebRef.current();
      if (unsubLogoRef.current) unsubLogoRef.current();
      if (unsubViewsRef.current) unsubViewsRef.current();
    };
  }, []);

  useEffect(() => {
    let unsubMailing = null;

    if (currentUser && currentUser.uid) {
      unsubMailing = onSnapshot(doc(db, 'mailingList', currentUser.uid), (docSnap) => {
        if (docSnap.exists() && docSnap.data().userName) {
          setDbUserName(docSnap.data().userName);
        } else {
          setDbUserName('');
        }
      });
    } else {
      setDbUserName('');
    }

    return () => {
      if (unsubMailing) unsubMailing();
    };
  }, [currentUser]);

  // --- MASTER PROMPT STATE MACHINE ---
  useEffect(() => {
    if (unsubPermsRef.current) {
      unsubPermsRef.current();
      unsubPermsRef.current = null;
    }

    const isLogout = prevUserRef.current && !currentUser;
    prevUserRef.current = currentUser;

    if (!currentUser || !currentUser.email) {
      setUserPermissions({
        isListed: false,
        canUpdate: false,
        canPromo: false,
        isAdmin: false,
      });

      if (isLogout) {
        handledStateRef.current = 'guest';
      } else if (!isAuthRoute && handledStateRef.current !== 'guest') {
        setShowLocationPrompt(true);
        handledStateRef.current = 'guest';
      }
      return;
    }

    const emailKey = currentUser.email.toLowerCase().trim();
    const currentUid = currentUser.uid;

    unsubPermsRef.current = onSnapshot(doc(db, 'permittedUsers', emailKey), (docSnap) => {
      let hasInitialViewId = false;

      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserPermissions({
          isListed: true,
          canUpdate: !!data.canUpdateMarkers,
          canPromo: !!data.canAccessPromo,
          isAdmin: !!data.isAdmin,
        });

        if (data.initialViewId) {
          hasInitialViewId = true;
        }
      } else {
        setUserPermissions({
          isListed: false,
          canUpdate: false,
          canPromo: false,
          isAdmin: false,
        });
      }

      if (!isAuthRoute && handledStateRef.current !== currentUid) {
        if (hasInitialViewId) {
          handledStateRef.current = currentUid;
        } else {
          setShowLocationPrompt(true);
          handledStateRef.current = currentUid;
        }
      }
    });

    return () => {
      if (unsubPermsRef.current) unsubPermsRef.current();
    };
  }, [currentUser, location.pathname, location.hash, isAuthRoute]);

  useEffect(() => {
    const unsubCats = onSnapshot(collection(db, 'categories'), (snapshot) => {
      const catsArray = [];
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        catsArray.push({
          id: doc.id,
          name: data.name || data.title || doc.id,
        });
      });

      catsArray.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(catsArray);
    });

    return () => {
      if (unsubCats) unsubCats();
    };
  }, []);

  useEffect(() => {
    if (viewConfig) {
      setActiveView((prev) => prev || viewConfig);
    }
  }, [viewConfig]);

  const fetchAndSetUserLocation = () => {
    setIsLocating(true);

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locView = {
            id: 'user-location',
            name: "YOUR LOC'N",
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            zoom: 8.7,
          };
          setUserLocationView(locView);

          const newConfig = {
            id: locView.id,
            name: locView.name,
            center: { lat: locView.lat, lng: locView.lng },
            zoom: locView.zoom,
          };

          setActiveView(newConfig);
          if (resetMap) resetMap(newConfig);

          setIsLocating(false);
        },
        (error) => {
          console.warn('[TopBar] Geolocation declined or unavailable:', error.message);
          setIsLocating(false);
        },
        { timeout: 10000, enableHighAccuracy: true, maximumAge: 0 },
      );
    } else {
      setIsLocating(false);
    }
  };

  const handleLocationConfirm = () => {
    setShowLocationPrompt(false);
    fetchAndSetUserLocation();
  };

  const handleNavClick = (callback) => callback();
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    if (unsubWebRef.current) unsubWebRef.current();
    if (unsubLogoRef.current) unsubLogoRef.current();
    if (unsubPermsRef.current) unsubPermsRef.current();
    if (unsubViewsRef.current) unsubViewsRef.current();
    if (resetFilter) resetFilter();
    setActiveMenu('HOME');
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  const handleChangePassword = () => {
    handleMenuClose();
    navigate('/change-password');
  };

  const handleMainWebSiteClick = () => {
    handleNavClick(() => {
      let urlToOpen = mainWebSiteUrl;
      if (!urlToOpen) return;
      if (!urlToOpen.startsWith('http://') && !urlToOpen.startsWith('https://')) {
        urlToOpen = 'https://' + urlToOpen;
      }
      window.open(urlToOpen, '_blank');
    });
  };

  const handleResetViewClick = () => {
    handleNavClick(() => {
      if (resetMap && activeView) resetMap(activeView);
    });
  };

  const handleInitialViewClick = (e) => {
    setInitialViewAnchorEl(e.currentTarget);
  };

  const handleInitialViewSelect = (view) => {
    setInitialViewAnchorEl(null);
    handleNavClick(() => {
      const newConfig = {
        id: view.id,
        name: view.name,
        center: { lat: parseFloat(view.lat), lng: parseFloat(view.lng) },
        zoom: parseFloat(view.zoom),
      };
      setActiveView(newConfig);
      if (resetMap) resetMap(newConfig);
    });
  };

  const handleYourLocationClick = () => {
    setInitialViewAnchorEl(null);
    handleNavClick(() => {
      if (userLocationView) {
        const newConfig = {
          id: userLocationView.id,
          name: userLocationView.name,
          center: { lat: userLocationView.lat, lng: userLocationView.lng },
          zoom: userLocationView.zoom,
        };
        setActiveView(newConfig);
        if (resetMap) resetMap(newConfig);
      } else {
        fetchAndSetUserLocation();
      }
    });
  };

  const handleFilterClick = (e) => {
    if (currentUser && (activeMenu === 'VIEW_INFO' || activeMenu === 'MARKUP_MARKERS')) {
      handleNavClick(() => {
        if (setShowFilter) setShowFilter(true);
      });
    } else {
      setFilterAnchorEl(e.currentTarget);
    }
  };

  const menuButtonSx = {
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    color: '#000',
    px: 0,
    minWidth: 'unset',
    transition: 'all 0.2s ease',
    '&:hover': {
      border: '1.5px solid #0055ff',
      borderRadius: '6px',
      padding: '2px 6px',
      backgroundColor: 'transparent',
    },
  };

  const toolButtonSx = {
    textTransform: 'none',
    px: 1,
    minWidth: 'unset',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    transition: 'all 0.25s ease',
    '& svg': { transition: 'all 0.25s ease' },
    '&:hover': {
      backgroundColor: 'rgba(0, 85, 255, 0.08)',
      '& svg': {
        transform: 'scale(1.12)',
        filter: 'drop-shadow(0 0 2px rgba(0,85,255,0.4))',
      },
    },
  };

  const showViewInfo =
    userPermissions.isListed || userPermissions.canUpdate || userPermissions.isAdmin;
  const showUpdateTools = userPermissions.canUpdate || userPermissions.isAdmin;
  const showPromoTab = userPermissions.canPromo;
  const showAdminTab = userPermissions.isAdmin;

  const computedHeight = (48 * clientLogoScale) / 100;

  // --- RENDERING HELPERS ---
  const renderNavButtons = () => (
    <>
      {showHomeMenu && (
        <Button
          variant='text'
          sx={menuButtonSx}
          onClick={() => {
            navigate('/');
            setActiveMenu('HOME');
          }}>
          Home
        </Button>
      )}
      <Button variant='text' sx={menuButtonSx} onClick={() => setActiveMenu('NEWS')}>
        News
      </Button>
      {showViewInfo && (
        <Button
          variant='text'
          sx={menuButtonSx}
          onClick={() => setActiveMenu('VIEW_INFO')}>
          View Info
        </Button>
      )}
      {showUpdateTools && (
        <>
          <Button
            variant='text'
            sx={menuButtonSx}
            onClick={() => setActiveMenu('IMPORT_MARKERS')}>
            Import Markers
          </Button>
          <Button
            variant='text'
            sx={menuButtonSx}
            onClick={() => setActiveMenu('MARKUP_MARKERS')}>
            Update Markers
          </Button>
        </>
      )}
      {showPromoTab && (
        <Button variant='text' sx={menuButtonSx} onClick={() => setShowPromo(true)}>
          Promo
        </Button>
      )}
      {showAdminTab && (
        <Button variant='text' sx={menuButtonSx} onClick={() => setShowAdmin(true)}>
          Admin
        </Button>
      )}
    </>
  );

  const renderToolButtons = () => (
    <>
      <Tooltip title='Select View'>
        <Button
          variant='text'
          onClick={handleInitialViewClick}
          sx={{
            ...toolButtonSx,
            ml: 0.5,
            px: 0,
            '&:hover': { ...toolButtonSx['&:hover'], backgroundColor: 'transparent' },
          }}>
          <MenuInitialView sx={{ fontSize: 35 }} />
        </Button>
      </Tooltip>
      <Tooltip title='Reset View'>
        <Button variant='text' onClick={handleResetViewClick} sx={toolButtonSx}>
          <MenuResetView sx={{ fontSize: 26, color: 'black' }} />
        </Button>
      </Tooltip>
      <Tooltip title='Filter'>
        <Button variant='text' onClick={handleFilterClick} sx={toolButtonSx}>
          <MenuFilter sx={{ fontSize: 26 }} />
        </Button>
      </Tooltip>
    </>
  );

  const renderUserSection = () => {
    if (currentUser) {
      const displayNameToUse = dbUserName || currentUser.displayName || '';
      const avatarLetter = displayNameToUse
        ? displayNameToUse.charAt(0).toUpperCase()
        : 'U';

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {displayNameToUse && (
            <Typography sx={{ color: '#000', fontWeight: 500, fontSize: '1rem' }}>
              {displayNameToUse}
            </Typography>
          )}
          <Avatar
            src={currentUser?.photoURL || undefined}
            slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
            sx={{
              width: 38,
              height: 38,
              bgcolor: '#e0e0e0',
              color: '#3f51b5',
              fontWeight: 'bold',
            }}>
            {!currentUser?.photoURL && avatarLetter}
          </Avatar>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon sx={{ color: '#000' }} />
          </IconButton>
        </Box>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title='Subscribe to our Newsfeed'>
            <Button
              variant='text'
              sx={{ ...toolButtonSx, ml: 0 }}
              onClick={() => setShowSubscribe(true)}>
              <MenuSubscribe sx={{ fontSize: 135 }} />
            </Button>
          </Tooltip>
        </Box>
      );
    }
  };

  return (
    <>
      <GlobalStyles styles={{ '.pac-container': { zIndex: '100020 !important' } }} />
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)',
          borderBottom: '1px solid rgba(0,0,0,0.25)',
          boxShadow: 'none',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: 'opacity 0.2s, filter 0.2s',
        }}>
        <Toolbar
          disableGutters
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            width: '100%',
            minHeight: 'auto',
          }}>
          {/* LOGOS */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              pl: 4,
              pr: 4,
              flexShrink: 0,
            }}>
            <img
              src={Logo}
              alt='LoKtr Logo'
              style={{ height: 40, width: 'auto', cursor: 'pointer' }}
              onClick={() => window.open('https://www.loktr.net', '_blank')}
              title='Visit www.loktr.net'
            />
            <Box
              onClick={handleMainWebSiteClick}
              title='Visit Main Website'
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 4px rgba(0,0,0,0.1)',
                width: 'auto',
                height: 'auto',
                flexShrink: 0,
                cursor: mainWebSiteUrl ? 'pointer' : 'default',
              }}>
              {clientLogoUrl && (
                <img
                  src={clientLogoUrl}
                  alt='Client Logo'
                  style={{
                    height: `${computedHeight}px`,
                    width: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              flexGrow: 1,
              ml: '74px',
            }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: 48,
                width: '100%',
              }}>
              <Box sx={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                {renderNavButtons()}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, flexShrink: 0 }}>
                {renderUserSection()}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 48,
                width: '100%',
                transform: 'translateY(-3px)',
              }}>
              {renderToolButtons()}
              <Box sx={{ flexGrow: 1, ml: 1 }}>
                <SearchBar
                  resetMap={resetMap}
                  setSearchMarker={setSearchMarker}
                  isLocked={isLocked}
                />
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* --- ALL DROPDOWN MENUS --- */}
      {currentUser && (
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          sx={{ zIndex: 100020 }}
          slotProps={{
            paper: {
              elevation: 4,
              sx: { borderRadius: '12px', py: 0.5, minWidth: 180 },
            },
          }}>
          <MenuItem
            onClick={handleChangePassword}
            disabled={isGoogleUser}
            sx={{ gap: 1.5, py: 1 }}
            title={
              isGoogleUser ? 'Google users manage their password through Google' : ''
            }>
            Change Password
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ gap: 1.5, py: 1 }}>
            Sign out
          </MenuItem>
        </Menu>
      )}

      <Menu
        anchorEl={initialViewAnchorEl}
        open={Boolean(initialViewAnchorEl)}
        onClose={() => setInitialViewAnchorEl(null)}
        sx={{ zIndex: 100020 }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: { borderRadius: '12px', py: 0.5, minWidth: 180 },
          },
        }}>
        <MenuItem
          key='user-location-action'
          selected={activeView?.id === 'user-location'}
          onClick={handleYourLocationClick}
          sx={{
            gap: 1.5,
            py: 1,
            color: '#000',
            borderBottom: initialViews.length > 0 ? '1px solid rgba(0,0,0,0.08)' : 'none',
          }}>
          YOUR LOC'N
        </MenuItem>

        {initialViews.map((view) => (
          <MenuItem
            key={view.id}
            selected={activeView?.id === view.id}
            onClick={() => handleInitialViewSelect(view)}
            sx={{ gap: 1.5, py: 1 }}>
            {view.name || 'Unnamed View'}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        sx={{ zIndex: 100020 }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: { borderRadius: '12px', py: 0.5, minWidth: 200, maxWidth: 400 },
          },
        }}>
        <MenuItem
          onClick={() => {
            setFilterAnchorEl(null);
            if (resetFilter) resetFilter();
            if (setSelectedCategory) setSelectedCategory(null);
          }}
          sx={{
            fontWeight: 'bold',
            borderBottom: '1px solid #eee',
            mb: 0.5,
            whiteSpace: 'nowrap',
          }}>
          All Categories
        </MenuItem>
        {categories.map((cat) => (
          <MenuItem
            key={cat.id}
            onClick={() => {
              setFilterAnchorEl(null);
              if (setSelectedCategory) setSelectedCategory(cat.id);
            }}
            sx={{ whiteSpace: 'nowrap' }}>
            {cat.name}
          </MenuItem>
        ))}
      </Menu>

      {/* --- MODALS & BACKDROPS --- */}
      <SubscribeDialog open={showSubscribe} onClose={() => setShowSubscribe(false)} />

      <Backdrop
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          zIndex: 100002,
        }}
        open={isLocating}>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress sx={{ color: '#00B0FF' }} size={60} thickness={4} />
          <Typography variant='h6' sx={{ color: '#000', fontWeight: 500 }}>
            Acquiring your location...
          </Typography>
        </Box>
      </Backdrop>

      <ConfirmDialog
        open={showLockAlert}
        onClose={() => setShowLockAlert(false)}
        onConfirm={() => setShowLockAlert(false)}
        message='Do you want to discard those changes?'
        autoClose={2000}
        sx={{ zIndex: 100005, '& .MuiBackdrop-root': { backgroundColor: 'transparent' } }}
        severity='warning'
      />
      <ConfirmDialog
        open={showLocationPrompt}
        onClose={() => setShowLocationPrompt(false)}
        onConfirm={handleLocationConfirm}
        message='Would you like to use your current location to center the map?'
        severity='info'
        sx={{ zIndex: 100005 }}
      />
    </>
  );
}
